import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {CreditResource} from '../resource/credit.resource';
import {CreditBundle, UserBalanceDetail} from '../interfaces/credit.interface';
import {AiStateService} from '../../services/ai-state.service';
import {CreditsPurchaseDialogComponent} from '../../dialogs/credits-purchase-dialog.component';

@Injectable({providedIn: 'root'})
export class CreditApiService {
  // Shared balance stream so any view that shows the user's remaining credits
  // (e.g. the smap assistant's expanded header) stays in sync when balance
  // changes from another part of the UI (CREATE SMAP generation, credit
  // purchase, ensureCredits check).
  private balanceSubject = new Subject<UserBalanceDetail>();
  public balanceChanged$: Observable<UserBalanceDetail> = this.balanceSubject.asObservable();

  constructor(
    private resource: CreditResource,
    private aiStateService: AiStateService,
  ) {
  }

  getBalance(): Observable<UserBalanceDetail> {
    return this.resource.getBalance().pipe(
        tap(balance => this.balanceSubject.next(balance))
    );
  }

  refreshBalance(): void {
    this.getBalance().subscribe();
  }

  getBundles(): Observable<CreditBundle[]> {
    return this.resource.getBundles();
  }

  purchaseBundle(bundleId: string): Observable<UserBalanceDetail> {
    return this.resource.purchaseBundle(bundleId).pipe(
        tap(balance => this.balanceSubject.next(balance))
    );
  }

  /**
   * Checks if user has credits to generate. If depleted and not using custom API keys,
   * opens the purchase dialog. Returns Observable<boolean> — true to proceed, false to abort.
   */
  ensureCredits(dialog: MatDialog, objectToGenerate: string): Observable<boolean> {
    if (this.aiStateService.isUsingCustomKeys()) {
      return of(true);
    }

    return this.getBalance().pipe(
      switchMap(balance => {
        if (!balance.isDepleted) {
          return of(true);
        }

        const ref = dialog.open(CreditsPurchaseDialogComponent, {
          width: '780px',
          autoFocus: false,
          data: {objectToGenerate},
        });

        return ref.afterClosed().pipe(
          map((result: UserBalanceDetail | null) => !!result)
        );
      })
    );
  }
}
